import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { StatusChip } from "../design/StatusChip";
import { send } from "../api/auth";
import { helpApi, type PermissionRef, type ReferenceReply, type RoleRef } from "../api/help";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { NAV_ITEMS } from "../system/navigation";
import { SCREENS } from "../system/screens";
import { Tabs } from "../design/Tabs";
import { ManualView, manualIsShown } from "../manual/ManualView";

// ---------------------------------------------------------------------------
// HELP. Run 76.
//
// ★ THE QUESTION THIS SCREEN EXISTS FOR IS "WHY CAN I NOT SEE THAT", and it is
// the one question in an RBAC product whose answer is already in the browser
// and has never been shown to anybody. The menu hides an entry when the session
// does not hold its permission, correctly and silently — so a colleague says
// "it's under Clients" and the person looking has no Clients, no error, and
// nothing to search for.
//
// EVERYTHING HERE IS DERIVED. The menu comes from NAV_ITEMS, the screens from
// SCREENS, the held permissions from the session, and the sentences from
// `app.permission` through one reference call. NOTHING ON THIS PAGE IS A LIST
// WRITTEN HERE — a hand-kept copy of the menu is the second answer that stops
// agreeing, which is the defect this repository has now recorded four times in
// one run.
//
// AND IT DEGRADES RATHER THAN BREAKS. The app and the Worker ship separately,
// so there is a window where this screen exists and `/v1/reference/permissions`
// does not. In that window every question below is still answered, in
// permission ids instead of sentences. A help screen that fails in exactly the
// window somebody is most likely to be confused would be worse than no help
// screen at all.
// ---------------------------------------------------------------------------


/** The one question. Every console menu entry, and whether this session reaches it. */
function WhyCantISee({
  held,
  permissions,
  roles,
}: {
  held: readonly string[];
  permissions: Map<string, PermissionRef>;
  roles: RoleRef[];
}) {
  const { locale, t } = useT();
  const items = NAV_ITEMS.filter((i) => i.surface === "console");
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Why can I not see something?")}</h2>
        <StatusChip tone="neutral" label={t("{n} not yours", { n: items.filter((i) => !held.includes(i.requires)).length })} />
      </div>
      <p className="muted">
        {t("The menu hides an entry when your role does not hold the permission beside it. That is correct and it is silent, which is why this table exists. Nothing here decides what you may do — the server does, and again the database.")}
      </p>
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">{t("Menu entry")}</th>
            <th scope="col">{t("Needs")}</th>
            <th scope="col">{t("You")}</th>
            <th scope="col">{t("Held by")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const yours = held.includes(item.requires);
            const ref = permissions.get(item.requires);
            const holders = roles
              .filter((r) => !r.is_platform && r.permissions.includes(item.requires))
              .map((r) => translateLabel(locale, r.label));
            return (
              <tr key={item.id}>
                <th scope="row">{translateLabel(locale, item.label)}</th>
                {/* The permission by its name, never its key (event.clinic tour, entry 77). */}
                <td>{ref ? translateLabel(locale, ref.label) : <span className="muted">{t("A permission without a name yet")}</span>}</td>
                <td>
                  {yours ? <span className="muted">—</span> : <StatusChip tone="neutral" label={t("Not yours")} />}
                </td>
                <td className="muted">{holders.length > 0 ? holders.join(", ") : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="muted">
        {t("Ask somebody who can manage members — an Owner or an Administrator — if you need a role that holds one of these.")}
      </p>
    </section>
  );
}

/** What this session holds, in sentences where the server could supply one. */
function YourAccess({
  held,
  permissions,
}: {
  held: readonly string[];
  permissions: Map<string, PermissionRef>;
}) {
  const { locale, t } = useT();
  const { me } = useSession();
  const org = me?.authenticated ? me.active_org : null;
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("What you can do")}</h2>
        <StatusChip tone="live" label={t("{n} permissions", { n: held.length })} />
      </div>
      <dl className="facts">
        <dt>{t("Organisation")}</dt>
        <dd>{org ? org.name : t("None chosen")}</dd>
        <dt>{t("Role")}</dt>
        <dd>{org?.role_label ? translateLabel(locale, org.role_label) : "—"}</dd>
      </dl>
      <ul className="plain-list">
        {/* What each permission lets you do, in words; one without words is left out rather than shown as a key. */}
        {[...held].sort().flatMap((id) => {
          const ref = permissions.get(id);
          return ref ? [<li key={id}>{translateLabel(locale, ref.description)}</li>] : [];
        })}
      </ul>
    </section>
  );
}

// S3: these were typed as "ten" and "thirty-five" in the one screen that says it derives
// everything, and both had gone stale.
const CONSOLE_MENU = NAV_ITEMS.filter((i) => i.surface === "console").length;
const CONSOLE_SCREENS = SCREENS.filter((s) => s.home === "console" && s.path !== "*").length;

/** Every screen this surface has, searchable. The menu is not the product. */
function EveryScreen({ query, onQuery }: { query: string; onQuery: (q: string) => void }) {
  const { locale, t } = useT();
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SCREENS.filter((s) => s.home === "console")
      .filter((s) => q === "" || translateLabel(locale, s.title).toLowerCase().includes(q) || s.title.toLowerCase().includes(q))
      .slice()
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [query, locale]);

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Every screen in the console")}</h2>
        <StatusChip tone="neutral" label={t("{count} shown", { count: rows.length })} />
      </div>
      <p className="muted">
        {t("The menu has {menu} entries and the console has {screens} screens. A screen that belongs to one record, such as an event, opens from that record.", { menu: CONSOLE_MENU, screens: CONSOLE_SCREENS })}
      </p>
      <div className="field">
        <label htmlFor="help-search">{t("Search screens")}</label>
        <input
          id="help-search"
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder={t("timesheet, proof, risk…")}
        />
        <p className="field-hint">{t("Matches the screen name.")}</p>
      </div>
      {rows.length === 0 ? (
        <p className="notice notice-neutral">{t("No screen matches that.")}</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">{t("Screen")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id}>
                {/* No address and no build slice: a route with ":id" and a code such as V12 are the developer's
                    words (event.clinic tour, entries 128 and 129). A screen that opens on its own is a link. */}
                <th scope="row">
                  {s.path.includes(":") || s.path === "*" ? (
                    <>
                      {translateLabel(locale, s.title)} <span className="muted">· {t("opens from its record")}</span>
                    </>
                  ) : (
                    <Link to={`/console${s.path === "" ? "" : `/${s.path}`}`}>{translateLabel(locale, s.title)}</Link>
                  )}
                </th>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}


export function Help() {
  const { t } = useT();
  const { apiBase, me } = useSession();
  const [reference, setReference] = useState<ReferenceReply | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    void (async () => {
      const res = await send<ReferenceReply>(helpApi.reference(apiBase));
      // A failure is not an error state on this screen. See the header: the
      // labels are an enrichment, and every question below is answerable
      // without them.
      setReference(res.status === 200 ? res.body : {});
    })();
  }, [apiBase]);

  const held = me?.authenticated ? me.permissions : [];
  const permissions = useMemo(() => {
    const m = new Map<string, PermissionRef>();
    for (const p of reference?.permissions ?? []) m.set(p.id, p);
    return m;
  }, [reference]);
  const roles = reference?.roles ?? [];

  return (
    <div className="stack">
      <h1>{t("Help")}</h1>
      <Tabs
        label={t("Help")}
        tabs={[
          ...(manualIsShown() ? [{ id: "manual", label: t("Manual"), content: <ManualView /> }] : []),
          {
            id: "visibility",
            label: t("Why can I not see something?"),
            content: (
              <div className="stack">
                <WhyCantISee held={held} permissions={permissions} roles={roles} />
                <YourAccess held={held} permissions={permissions} />
              </div>
            ),
          },
          { id: "screens", label: t("Every screen in the console"), content: <EveryScreen query={query} onQuery={setQuery} /> },
        ]}
      />
    </div>
  );
}
